import { pricingPlansModalOpenAtom } from '../../billingAtoms';
import { usePracticeSeatPrices } from '../../hooks/usePracticeSeatPrices';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';

import { Period } from '@heidi/commerce/contracts';
import { isMaxSeatRecommended, useQueryWorkspaceTeamUsage } from '@heidi/commerce/data';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption } from '@heidi/design-system/typography';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo, useWorkspaceId } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { ExternalLink } from 'lucide-react';
import { useCallback, type MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PracticeSeatUpgradeCardFromPlan } from '../../../../trials-and-upgrades/practice-seat-upgrade-card/practice-seat-upgrade-card';
import { PracticeSeatUpgradeCard } from '../../../../trials-and-upgrades/practice-seat-upgrade-card/practice-seat-upgrade-card';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';
import { trackPricingPlansModalCta } from './track-pricing-plans-modal-cta';
import type { UsePricingPlansModalStateReturn } from './usePricingPlansModalState';

const SEAT_CHECKOUT_ROUTE = '/settings/billing/upgrade';

type PracticeSeatUpgradePlansProps = Pick<
  UsePricingPlansModalStateReturn,
  'currentPlan' | 'currentSeatType'
> & {
  /** `practice`: Standard team picking Max seats. `scribeTeam`: moving the team to Practice. */
  fromPlan: PracticeSeatUpgradeCardFromPlan;
};

/** The pricing plans modal body for a Practice Standard or Scribe Team team: one Practice card into the seat checkout. */
export function PracticeSeatUpgradePlans({
  currentPlan,
  currentSeatType,
  fromPlan,
}: PracticeSeatUpgradePlansProps) {
  const router = getCommerceBillingUiPorts().useAppRouter();
  const { formatMessage } = useIntl();
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const { standardSeatPrice, maxSeatPrice } = usePracticeSeatPrices();
  const {
    hasTeam,
    isTeamStripeManaged,
    isNonAdminTeamMember,
    isLoadingTeamDetails,
    isTeamDataReady,
  } = useCombinedQueriesTeamGeneralInfo();

  // Same holds as the multi-card body: stay closed until the team role settles,
  // then block non-admins and teams Stripe does not bill.
  const teamRoleUnresolved = !!isLoadingTeamDetails || isTeamDataReady === false;
  const settledNonAdmin = !!isNonAdminTeamMember && !teamRoleUnresolved;
  const isNonStripeManagedTeam = hasTeam && !isTeamStripeManaged && !teamRoleUnresolved;
  const ctaDisabledTooltip = settledNonAdmin
    ? formatMessage(pricingPlansModalMessages.contactTeamAdminToUpgrade)
    : isNonStripeManagedTeam
      ? formatMessage(pricingPlansModalMessages.contactSalesToUpgrade)
      : undefined;
  const workspaceId = useWorkspaceId();
  const { data: teamUsage } = useQueryWorkspaceTeamUsage({
    workspaceId,
    enabled: !teamRoleUnresolved && !settledNonAdmin,
  });

  const handleGetPractice = useCallback(() => {
    trackPricingPlansModalCta({
      action: ButtonAction.PricingPlansModalGetPlan,
      label: formatMessage(pricingPlansModalMessages.getPractice),
      currentPlan,
      currentSeatType,
      targetTier: SubscriptionTier.CLINICIAN_PRO_PLUS,
      targetSeatType: BillingSeatType.MAX,
      period: Period.ONE_YEAR,
      isInReverseTrial: false,
      isFakeClinicianTrial: false,
    });
    setPricingPlansModalOpen(false);
    void router.push(SEAT_CHECKOUT_ROUTE);
  }, [currentPlan, currentSeatType, formatMessage, router, setPricingPlansModalOpen]);

  const handleTalkToSales = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      trackPricingPlansModalCta({
        action: ButtonAction.PricingPlansModalGetInTouch,
        label: formatMessage(pricingPlansModalMessages.talkToSalesCta),
        currentPlan,
        currentSeatType,
        targetTier: SubscriptionTier.ENTERPRISE,
        isInReverseTrial: false,
        isFakeClinicianTrial: false,
      });
      // The desktop webview ignores target="_blank"; hand the URL to the shell.
      if (getCommerceBillingUiPorts().isTauri()) {
        event.preventDefault();
        void getCommerceBillingUiPorts().openUrlInTauri(
          getCommerceBillingUiPorts().billingUrls.GET_IN_TOUCH_URL
        );
      }
    },
    [currentPlan, currentSeatType, formatMessage]
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <PracticeSeatUpgradeCard
        standardSeatPrice={standardSeatPrice}
        maxSeatPrice={maxSeatPrice}
        onGetPractice={handleGetPractice}
        isCtaDisabled={teamRoleUnresolved || settledNonAdmin || isNonStripeManagedTeam}
        ctaDisabledTooltip={ctaDisabledTooltip}
        isMaxSeatRecommended={isMaxSeatRecommended(teamUsage?.ratio)}
        fromPlan={fromPlan}
      />
      <div className="flex flex-col items-center gap-1 text-center">
        <TypographyCaption className="text-text-secondary">
          <FormattedMessage {...pricingPlansModalMessages.salesTaxAndTermsNote} />
        </TypographyCaption>
        <TypographyCaption className="text-text-primary">
          <FormattedMessage
            {...pricingPlansModalMessages.talkToSales}
            values={{
              link: (
                <Link
                  href={getCommerceBillingUiPorts().billingUrls.GET_IN_TOUCH_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="inline"
                  size="inherit"
                  onClick={handleTalkToSales}
                >
                  <FormattedMessage {...pricingPlansModalMessages.talkToSalesCta} />
                  <ExternalLink className="size-3" aria-hidden />
                </Link>
              ),
            }}
          />
        </TypographyCaption>
      </div>
    </div>
  );
}
